import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import queryString from 'query-string';
import ReactPaginate from 'react-paginate';
import { useFavoriteProductsStore } from '../../../store/FavoriteProducts';
import FavoriteProductItem from './FavoriteProductItem';
import { filterProductsByStatus, applyPagination, getProductStatus } from './favoritesUtils';

const FavoritesTab = ({ history: parentHistory, props, productIdsInCart, onToggleModal, addProductToCart }) => {
  const history = useHistory();
  const location = useLocation();
  const numberPerPage = 21;
  
  const [allFavoriteProducts, setAllFavoriteProducts] = useState([]);
  const [favoriteProducts, setFavoriteProducts] = useState([]);
  const [isFavoriteProductsLoaded, setIsFavoriteProductsLoaded] = useState(false);
  const [isLoadingFavoriteProducts, setIsLoadingFavoriteProducts] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    const currentQueryParams = queryString.parse(location.search);
    const page = currentQueryParams.favoritesPage ? parseInt(currentQueryParams.favoritesPage) : 1;
    return page - 1;
  });
  const [pageCount, setPageCount] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState(() => {
    const currentQueryParams = queryString.parse(location.search);
    return currentQueryParams.status || null;
  });
  const [showFilters, setShowFilters] = useState(false);

  const favoriteProductsCount = useFavoriteProductsStore(state => state.favoriteProductsCount);
  const setFavoriteProductIds = useFavoriteProductsStore(state => state.setFavoriteProductIds);
  const removeFavoriteProduct = useFavoriteProductsStore(state => state.removeFavoriteProduct);
  const addFavoriteProductFromStore = useFavoriteProductsStore(state => state.addFavoriteProduct);

  // Завантажуємо обрані товари при відкритті табу (тільки один раз)
  useEffect(() => {
    const currentQueryParams = queryString.parse(location.search);
    if (currentQueryParams.tab === 'favorites') {
      if (!isFavoriteProductsLoaded) {
        loadFavoriteProducts();
      }
      // Якщо в URL є статус, автоматично відкриваємо панель фільтрів
      if (currentQueryParams.status) {
        setShowFilters(true);
      }
    }
  }, [location.search, isFavoriteProductsLoaded]);

  // Оновлюємо відображені товари при зміні сторінки або фільтра або всіх товарів
  useEffect(() => {
    if (isFavoriteProductsLoaded) {
      updateDisplayedProducts();
    }
  }, [allFavoriteProducts, location.search, isFavoriteProductsLoaded]);

  // Оновлюємо відображені товари на основі поточних параметрів з URL
  const updateDisplayedProducts = () => {
    const currentQueryParams = queryString.parse(location.search);
    const page = currentQueryParams.favoritesPage ? parseInt(currentQueryParams.favoritesPage) : 1;
    const status = currentQueryParams.status || null;
    const newCurrentPage = page - 1;
    
    // Оновлюємо state тільки якщо значення змінились
    if (newCurrentPage !== currentPage) {
      setCurrentPage(newCurrentPage);
    }
    if (status !== selectedStatus) {
      setSelectedStatus(status);
    }
    
    // Оновлюємо відображення з поточними параметрами з URL
    const filtered = filterProductsByStatus(allFavoriteProducts, status);
    const paginated = applyPagination(filtered, newCurrentPage, numberPerPage);
    const totalPages = Math.ceil(filtered.length / numberPerPage);
    
    setFavoriteProducts(paginated);
    setPageCount(totalPages);
  };

  // Завантажуємо всі обрані товари одним запитом (без пагінації)
  const loadFavoriteProducts = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      if (props && props.history) {
        props.history.push("/login");
      } else {
        history.push("/login");
      }
      return;
    }
    setIsLoadingFavoriteProducts(true);
    // Завантажуємо всі товари одним запитом з великим page size
    axios
      .get(`/api/v1/product-favorite/products?page=1&numberPerPage=1000`, {
        headers: {
          Authorization: token
        }
      })
      .then(res => {
        const allProducts = res.data.data || [];
        setAllFavoriteProducts(allProducts);
        setIsFavoriteProductsLoaded(true);
        setIsLoadingFavoriteProducts(false);
        // Оновлюємо store з ID товарів для синхронізації з ProductPage
        const favoriteProductIds = allProducts.map(product => product.id);
        setFavoriteProductIds(favoriteProductIds);
        // Оновлення відображення відбудеться через useEffect при зміні allFavoriteProducts
      })
      .catch(() => {
        setIsLoadingFavoriteProducts(false);
        if (props && props.history) {
          props.history.push("/login");
        } else {
          history.push("/login");
        }
      });
  };

  const removeFromFavorites = (productId) => {
    const token = localStorage.getItem("token");
    if (!token) {
      return;
    }

    // Зберігаємо поточний стан для відкату у випадку помилки
    const previousAllProducts = [...allFavoriteProducts];
    const currentQueryParams = queryString.parse(location.search);
    const currentPageNum = currentQueryParams.favoritesPage ? parseInt(currentQueryParams.favoritesPage) : 1;

    // Оптимістичне оновлення: одразу видаляємо товар зі всіх товарів
    const updatedAllProducts = allFavoriteProducts.filter(product => product.id !== productId);
    setAllFavoriteProducts(updatedAllProducts);

    // Оновлюємо store (оптимістичне оновлення кількості відбудеться автоматично в removeFavoriteProduct)
    removeFavoriteProduct(productId);

    // Перевіряємо чи поточна сторінка стала порожньою після видалення
    const currentStatusForCheck = currentQueryParams.status || null;
    const filtered = filterProductsByStatus(updatedAllProducts, currentStatusForCheck);
    const currentPageIndex = currentPageNum - 1;
    const paginated = applyPagination(filtered, currentPageIndex, numberPerPage);
    
    // Якщо поточна сторінка стала порожньою і є попередня сторінка - переходимо на неї
    if (paginated.length === 0 && currentPageNum > 1) {
      const previousPage = currentPageNum - 1;
      const params = queryString.stringify({
        ...currentQueryParams,
        favoritesPage: previousPage
      });
      history.push(`/user/personal-cabinet?${params}`);
    }

    // Відправляємо DELETE запит у фоні
    axios({
      method: 'delete',
      url: `/api/v1/product-favorite/product/${productId}`,
      headers: { Authorization: token }
    })
      .catch(error => {
        console.error('Error removing from favorites:', error);
        // Відкат: повертаємо товар назад
        setAllFavoriteProducts(previousAllProducts);
        // Повертаємо товар у store (оптимістичне оновлення кількості відбудеться автоматично в addFavoriteProductFromStore)
        const currentIds = useFavoriteProductsStore.getState().favoriteProductIds;
        if (!currentIds.includes(productId)) {
          addFavoriteProductFromStore(productId); // Це автоматично оновить кількість та favoriteProductIds
        }
      });
  };

  const onClickPage = (data) => {
    const selectedPage = data.selected + 1;
    const currentQueryParams = queryString.parse(location.search);
    const params = queryString.stringify({
      ...currentQueryParams,
      favoritesPage: selectedPage
    });
    history.push(`/user/personal-cabinet?${params}`);
    window.scrollTo(0, 0);
  };

  const handleStatusFilterChange = (status) => {
    const currentQueryParams = queryString.parse(location.search);
    const params = queryString.stringify({
      ...currentQueryParams,
      status: status || undefined,
      favoritesPage: 1
    });
    history.push(`/user/personal-cabinet?${params}`);
  };

  // Унікальні статуси серед обраних товарів (лише ті, для яких є товари)
  const getAvailableStatuses = () => {
    if (!allFavoriteProducts || allFavoriteProducts.length === 0) return [];
    const statuses = allFavoriteProducts.map(p => getProductStatus(p)).filter(s => s != null);
    return [...new Set(statuses)];
  };

  const allFavoriteStatuses = [
    { value: 'Є в наявності', label: 'Є в наявності' },
    { value: 'Останні набори', label: 'Останні набори' },
    { value: 'Передзамовлення', label: 'Передзамовлення' },
    { value: 'Тимчасово недоступний', label: 'Тимчасово недоступний' },
    { value: 'Під замовлення', label: 'Під замовлення' },
    { value: 'Знятий з продажу', label: 'Знятий з продажу' }
  ];
  const availableStatuses = getAvailableStatuses();
  const favoriteStatuses = [
    { value: null, label: 'Всі' },
    ...allFavoriteStatuses.filter(s => availableStatuses.includes(s.value))
  ];
  const showFiltersSection = favoriteStatuses.length > 2; // не показувати фільтри, якщо лише 1 статус

  const getStatusButtonClass = (statusValue) => {
    const isActive = selectedStatus === statusValue;
    if (statusValue === null) {
      return `btn btn-sm ${isActive ? 'btn-primary' : 'btn-outline-primary'} mb-2 mb-md-0`;
    }
    const statusColors = {
      'Є в наявності': { active: 'btn-success', inactive: 'btn-outline-success' },
      'Останні набори': { active: 'btn-info', inactive: 'btn-outline-info' },
      'Передзамовлення': { active: 'btn-success', inactive: 'btn-outline-success' },
      'Тимчасово недоступний': { active: 'btn-warning', inactive: 'btn-outline-warning' },
      'Під замовлення': { active: 'btn-warning', inactive: 'btn-outline-warning' },
      'Знятий з продажу': { active: 'btn-danger', inactive: 'btn-outline-danger' }
    };
    const colors = statusColors[statusValue] || { active: 'btn-secondary', inactive: 'btn-outline-secondary' };
    return `btn btn-sm ${isActive ? colors.active : colors.inactive} mb-2 mb-md-0`;
  };

  return (
    <div className="row personal-cabinet-tab-content">
      <div className='col'>
        <h5 className='mb-2 text-center d-flex align-items-center justify-content-center' style={{ gap: '0.5rem' }}>
          {showFiltersSection && (
            <i 
              className={`bi bi-funnel${showFilters ? '-fill' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
              style={{ 
                fontSize: '1.1rem',
                cursor: 'pointer',
                color: showFilters ? '#007bff' : '#6c757d',
                transition: 'color 0.2s ease'
              }}
              title='Фільтр по статусу'
              onMouseEnter={(e) => e.currentTarget.style.color = '#007bff'}
              onMouseLeave={(e) => e.currentTarget.style.color = showFilters ? '#007bff' : '#6c757d'}
            />
          )}
          <b>Обрані товари:</b>
        </h5>
        {favoriteProductsCount > 0 ? (
          <>
            {showFiltersSection && showFilters && (
              <div className='mb-3 d-flex flex-wrap justify-content-center' style={{ gap: '0.5rem' }}>
                {favoriteStatuses.map((status) => (
                  <button
                    key={status.value ?? 'all'}
                    type='button'
                    className={getStatusButtonClass(status.value)}
                    onClick={() => handleStatusFilterChange(status.value)}
                  >
                    {status.label}
                  </button>
                ))}
              </div>
            )}
            {showFiltersSection && selectedStatus && (
              <div className='mb-3 d-flex flex-wrap align-items-center justify-content-center' style={{ gap: '0.5rem' }}>
                <span className='font-weight-bold' style={{ fontSize: '0.875rem' }}>Активний фільтр:</span>
                <span className='badge badge-dark d-inline-flex align-items-center' style={{ fontSize: '0.875rem', padding: '0.35rem 0.5rem', fontWeight: 'bold' }}>
                  {selectedStatus}
                  <button
                    type='button'
                    className='btn p-0 ml-2 border-0 bg-transparent'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStatusFilterChange(null);
                    }}
                    title='Скинути фільтр'
                    style={{ fontSize: '0.875rem', color: 'inherit', lineHeight: '1', padding: 0, cursor: 'pointer', opacity: 0.8 }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                  >
                    <i className='bi bi-x' style={{ fontWeight: 'bold' }}></i>
                  </button>
                </span>
              </div>
            )}
            {isLoadingFavoriteProducts ? (
              <p className='text-center'>Завантажуємо...</p>
            ) : favoriteProducts.length === 0 ? (
              <p className='text-center font-weight-bold'>Товарів в такому статусі немає в обраних</p>
            ) : (
              <div className='row'>
                {favoriteProducts.map(product => (
                  <FavoriteProductItem
                    key={product.id}
                    product={product}
                    productIdsInCart={productIdsInCart}
                    onRemoveFromFavorites={removeFromFavorites}
                    onToggleModal={onToggleModal}
                    onAddToCart={addProductToCart}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <p className='text-center mt-3'>Додавайте товари до обраних на сторінці товару, щоб швидко знайти їх пізніше</p>
        )}
        {favoriteProductsCount > 0 && !isLoadingFavoriteProducts && favoriteProducts.length > 0 && pageCount > 1 && (
          <div className='center mt-3'>
            <ReactPaginate
              previousLabel={'<'}
              nextLabel={'>'}
              breakLabel={'...'}
              breakClassName={'break-me'}
              pageCount={pageCount}
              marginPagesDisplayed={1}
              pageRangeDisplayed={2}
              onPageChange={onClickPage}
              containerClassName={'pagination'}
              activeClassName={'active'}
              forcePage={currentPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesTab;
